import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Pagination,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import registrationService from "../../services/registrationService";
import departmentService from "../../services/departmentService";
import classService from "../../services/classService";

const PAGE_LIMIT = 20;

const RegistrationSettings = () => {
  const [activeTab, setActiveTab] = useState(0);

  // ─── PRN Management State ───
  const [prnInput, setPrnInput] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addSuccess, setAddSuccess] = useState(null);

  // Pre-assigned fields for single add
  const [addDepartmentId, setAddDepartmentId] = useState("");
  const [addClassId, setAddClassId] = useState("");
  const [addRollNumber, setAddRollNumber] = useState("");

  // Departments & classes lookup for pre-assigned dropdowns
  const [departments, setDepartments] = useState([]);
  const [deptLoading, setDeptLoading] = useState(false);
  const [classesByDept, setClassesByDept] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);

  // PRN list
  const [prns, setPrns] = useState([]);
  const [prnListLoading, setPrnListLoading] = useState(false);
  const [prnListError, setPrnListError] = useState(null);
  const [prnPage, setPrnPage] = useState(1);
  const [prnTotal, setPrnTotal] = useState(0);

  // PRN list filters
  const [prnSearch, setPrnSearch] = useState("");
  const [prnStatusFilter, setPrnStatusFilter] = useState("");
  const prnSearchDebounceRef = React.useRef(null);

  // Bulk import
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSummary, setImportSummary] = useState(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ─── Settings State ───
  const [availabilityEnabled, setAvailabilityEnabled] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState(null);
  const [settingsSuccess, setSettingsSuccess] = useState(null);

  // ─── Fetch PRN List ───
  const fetchPrns = useCallback(async (page = 1, search = "", status = "") => {
    setPrnListLoading(true);
    setPrnListError(null);
    try {
      const params = { page, limit: PAGE_LIMIT };
      if (search.trim()) {
        params.search = search.trim();
      }
      if (status) {
        params.status = status;
      }
      const res = await registrationService.listPrns(params);
      const data = res?.data?.data ?? res?.data ?? {};
      setPrns(data.rows ?? data.prns ?? []);
      setPrnTotal(data.total ?? data.count ?? 0);
    } catch (err) {
      setPrnListError(
        err?.response?.data?.message || "Failed to load PRN list."
      );
      setPrns([]);
    } finally {
      setPrnListLoading(false);
    }
  }, []);

  // ─── Fetch Availability ───
  const fetchAvailability = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const res = await registrationService.getAvailability_staff();
      const data = res?.data?.data ?? res?.data ?? {};
      setAvailabilityEnabled(data.enabled ?? data.selfRegistrationEnabled ?? false);
    } catch (err) {
      setSettingsError(
        err?.response?.data?.message || "Failed to load availability settings."
      );
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 0) {
      fetchPrns(prnPage, prnSearch, prnStatusFilter);
    }
  }, [activeTab, prnPage, prnSearch, prnStatusFilter, fetchPrns]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchAvailability();
    }
  }, [activeTab, fetchAvailability]);

  // ─── Fetch Departments for Add PRN Form ───
  useEffect(() => {
    if (activeTab === 0 && departments.length === 0) {
      setDeptLoading(true);
      departmentService
        .getDepartmentNames()
        .then((res) => {
          const data = res?.data?.data ?? res?.data ?? [];
          setDepartments(Array.isArray(data) ? data : []);
        })
        .catch(() => setDepartments([]))
        .finally(() => setDeptLoading(false));
    }
  }, [activeTab, departments.length]);

  // ─── Fetch Classes When Department Changes ───
  useEffect(() => {
    if (!addDepartmentId) {
      setClassesByDept([]);
      return;
    }
    setClassesLoading(true);
    setAddClassId(""); // reset class when department changes
    classService
      .getAllClasses({ page: 1, limit: 200, departmentId: addDepartmentId })
      .then((res) => {
        // classService.getAllClasses returns response.data which is { status, message, data: [...], pagination }
        const rows = res?.data ?? res?.classes ?? res?.rows ?? [];
        setClassesByDept(Array.isArray(rows) ? rows : []);
      })
      .catch(() => setClassesByDept([]))
      .finally(() => setClassesLoading(false));
  }, [addDepartmentId]);

  // ─── Add PRN Handler ───
  const handleAddPrn = async (e) => {
    e.preventDefault();
    const trimmed = prnInput.trim();
    if (!trimmed) return;

    setAddLoading(true);
    setAddError(null);
    setAddSuccess(null);
    try {
      const payload = { prn: trimmed };
      if (addDepartmentId) payload.departmentId = Number(addDepartmentId);
      if (addClassId) payload.classId = Number(addClassId);
      if (addRollNumber.trim()) payload.rollNumber = addRollNumber.trim();

      await registrationService.addPrn(payload);
      setAddSuccess(`PRN "${trimmed.toUpperCase()}" added successfully.`);
      setPrnInput("");
      setAddDepartmentId("");
      setAddClassId("");
      setAddRollNumber("");
      fetchPrns(prnPage, prnSearch, prnStatusFilter);
    } catch (err) {
      setAddError(
        err?.response?.data?.message || "Failed to add PRN."
      );
    } finally {
      setAddLoading(false);
    }
  };

  // ─── Bulk Import Handler ───
  const handleBulkImport = async () => {
    if (!importFile) return;

    setImportLoading(true);
    setImportError(null);
    setImportSummary(null);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const res = await registrationService.bulkImportPrns(formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = res?.data?.data ?? res?.data ?? {};
      setImportSummary(data);
      setImportFile(null);
      // Reset file input
      const fileInput = document.getElementById("prn-bulk-import-input");
      if (fileInput) fileInput.value = "";
      fetchPrns(prnPage, prnSearch, prnStatusFilter);
    } catch (err) {
      setImportError(
        err?.response?.data?.message || "Bulk import failed."
      );
    } finally {
      setImportLoading(false);
    }
  };

  // ─── Delete PRN Handler ───
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);
    try {
      await registrationService.deletePrn(deleteTarget.id);
      setDeleteTarget(null);
      fetchPrns(prnPage, prnSearch, prnStatusFilter);
    } catch (err) {
      setPrnListError(
        err?.response?.data?.message || "Failed to delete PRN."
      );
      setDeleteTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ─── Availability Toggle Handler ───
  const handleAvailabilityToggle = async () => {
    const newValue = !availabilityEnabled;
    setAvailabilityLoading(true);
    setSettingsError(null);
    setSettingsSuccess(null);
    try {
      await registrationService.setAvailability({ enabled: newValue });
      setAvailabilityEnabled(newValue);
      setSettingsSuccess(
        newValue
          ? "Registration is now open."
          : "Registration is now closed."
      );
    } catch (err) {
      setSettingsError(
        err?.response?.data?.message || "Failed to update availability."
      );
    } finally {
      setAvailabilityLoading(false);
    }
  };

  // Cleanup PRN search debounce timer on unmount
  useEffect(() => {
    return () => {
      if (prnSearchDebounceRef.current) clearTimeout(prnSearchDebounceRef.current);
    };
  }, []);

  const prnPageCount = Math.ceil(prnTotal / PAGE_LIMIT);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Registration Settings
      </Typography>

      <Paper sx={{ mt: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, val) => setActiveTab(val)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
        >
          <Tab label="PRN Management" />
          <Tab label="Settings" />
        </Tabs>

        {/* ═══ PRN Management Tab ═══ */}
        {activeTab === 0 && (
          <Box sx={{ p: 2 }}>
            {/* Add PRN Form */}
            <Box
              component="form"
              onSubmit={handleAddPrn}
              sx={{ mb: 2 }}
            >
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1 }}>
                <TextField
                  label="PRN"
                  size="small"
                  value={prnInput}
                  onChange={(e) => setPrnInput(e.target.value)}
                  placeholder="Enter PRN (e.g., ABC123)"
                  inputProps={{ maxLength: 50 }}
                  sx={{ minWidth: 220 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={addLoading || !prnInput.trim()}
                >
                  {addLoading ? <CircularProgress size={20} color="inherit" /> : "Add"}
                </Button>
              </Box>

              {/* Optional pre-assigned fields */}
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                Optionally pre-assign department, class, and roll number
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start" }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="add-prn-dept-label">Department</InputLabel>
                  <Select
                    labelId="add-prn-dept-label"
                    value={addDepartmentId}
                    label="Department"
                    onChange={(e) => setAddDepartmentId(e.target.value)}
                    disabled={deptLoading}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="add-prn-class-label">Class</InputLabel>
                  <Select
                    labelId="add-prn-class-label"
                    value={addClassId}
                    label="Class"
                    onChange={(e) => setAddClassId(e.target.value)}
                    disabled={!addDepartmentId || classesLoading}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {classesByDept.map((cls) => (
                      <MenuItem key={cls.id} value={cls.id}>
                        {cls.section ? `${cls.name} - ${cls.section}` : cls.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Roll Number"
                  size="small"
                  value={addRollNumber}
                  onChange={(e) => setAddRollNumber(e.target.value)}
                  placeholder="e.g., CS101"
                  inputProps={{ maxLength: 50 }}
                  sx={{ width: 140 }}
                />
              </Box>
            </Box>

            {addError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddError(null)}>
                {addError}
              </Alert>
            )}
            {addSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAddSuccess(null)}>
                {addSuccess}
              </Alert>
            )}

            {/* Bulk Import */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
              <input
                id="prn-bulk-import-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                style={{ display: "none" }}
              />
              <Button
                variant="outlined"
                component="label"
                htmlFor="prn-bulk-import-input"
                size="small"
              >
                {importFile ? importFile.name : "Choose File"}
              </Button>
              <Button
                variant="contained"
                onClick={handleBulkImport}
                disabled={importLoading || !importFile}
                size="small"
              >
                {importLoading ? <CircularProgress size={20} color="inherit" /> : "Upload"}
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  // Generate and download a sample template xlsx with optional pre-assigned columns
                  import("xlsx").then((XLSX) => {
                    const ws = XLSX.utils.aoa_to_sheet([
                      ["PRN", "departmentId", "classId", "rollNumber"],
                      ["ABC123", "5", "12", "CS101"],
                      ["DEF456", "5", "12", "CS102"],
                      ["GHI789", "", "", ""],  // backward compat — empty optional columns
                    ]);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "PRNs");
                    XLSX.writeFile(wb, "prn_import_template.xlsx");
                  });
                }}
              >
                Download Template
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              The template includes optional columns: <strong>departmentId</strong>, <strong>classId</strong>, and <strong>rollNumber</strong>.
              Leave them empty if you don't want to pre-assign structural data. Rows with invalid department or class IDs will be skipped.
            </Typography>

            {importError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImportError(null)}>
                {importError}
              </Alert>
            )}
            {importSummary && (
              <Alert severity="info" sx={{ mb: 2 }} onClose={() => setImportSummary(null)}>
                Import complete — Imported: {importSummary.imported ?? 0},
                Duplicates: {importSummary.duplicates ?? 0},
                Invalid: {importSummary.invalid ?? 0},
                Total rows: {importSummary.totalRows ?? importSummary.total ?? 0}
              </Alert>
            )}
            {importSummary?.skipped?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Skipped Rows ({importSummary.skipped.length}):
                </Typography>
                {importSummary.skipped.map((s, i) => (
                  <Typography key={i} variant="body2">
                    Row {s.row}: PRN &quot;{s.prn}&quot; — {s.reason}
                  </Typography>
                ))}
              </Alert>
            )}

            {/* PRN List Filters */}
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search by PRN..."
                onChange={(e) => {
                  const value = e.target.value;
                  if (prnSearchDebounceRef.current) {
                    clearTimeout(prnSearchDebounceRef.current);
                  }
                  prnSearchDebounceRef.current = setTimeout(() => {
                    setPrnSearch(value);
                    setPrnPage(1);
                  }, 400);
                }}
                sx={{ width: { xs: "100%", sm: 250 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="prn-status-filter-label">Status</InputLabel>
                <Select
                  labelId="prn-status-filter-label"
                  value={prnStatusFilter}
                  label="Status"
                  onChange={(e) => { setPrnStatusFilter(e.target.value); setPrnPage(1); }}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  <MenuItem value="unused">Unused</MenuItem>
                  <MenuItem value="used">Used</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* PRN List */}
            {prnListError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPrnListError(null)}>
                {prnListError}
              </Alert>
            )}

            {prnListLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            )}

            {!prnListLoading && prns.length === 0 && (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography color="text.secondary">
                  No PRNs found. Add a PRN above or use bulk import.
                </Typography>
              </Box>
            )}

            {!prnListLoading && prns.length > 0 && (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>PRN</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Class</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {prns.map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>{item.prn}</TableCell>
                          <TableCell>{item.department?.name || "—"}</TableCell>
                          <TableCell>{item.class ? (item.class.section ? `${item.class.name} - ${item.class.section}` : item.class.name) : "—"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={item.status === "unused" ? "Unused" : "Used"}
                              color={item.status === "unused" ? "success" : "default"}
                            />
                          </TableCell>
                          <TableCell>
                            {item.createdAt
                              ? new Date(item.createdAt).toLocaleDateString("en-IN", {
                                  timeZone: "Asia/Kolkata",
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {item.status === "unused" && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteTarget(item)}
                                aria-label={`Delete PRN ${item.prn}`}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {prnPageCount > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Pagination
                      count={prnPageCount}
                      page={prnPage}
                      onChange={(_e, page) => setPrnPage(page)}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* ═══ Settings Tab ═══ */}
        {activeTab === 1 && (
          <Box sx={{ p: 2 }}>
            {settingsError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSettingsError(null)}>
                {settingsError}
              </Alert>
            )}
            {settingsSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSettingsSuccess(null)}>
                {settingsSuccess}
              </Alert>
            )}

            {settingsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Registration Availability
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={availabilityEnabled}
                        onChange={handleAvailabilityToggle}
                        disabled={availabilityLoading}
                        color="primary"
                      />
                    }
                    label={availabilityEnabled ? "Registration is Open" : "Registration is Closed"}
                  />
                  {availabilityLoading && <CircularProgress size={20} />}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {availabilityEnabled
                    ? "Students can currently submit self-registration forms for your institute."
                    : "Self-registration is currently disabled. Students will see a closed message."}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete PRN <strong>{deleteTarget?.prn}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegistrationSettings;
